/**
 * Hook for the History Library.
 * Wraps store actions and handles async Drive uploads after generation.
 */

import { useCallback } from 'react';

import type { Gender } from '@/constants/enums';
import { useAppStore, getAppStore } from '@/store';
import type { IAppStore } from '@/store';
import type { GenerationResult, LookVariant } from '@/types';

function toVariant(result: GenerationResult): LookVariant {
  const { character } = result.request;
  return {
    id: result.id,
    generatedImageUrl: result.imageUrl,
    driveUidGenerated: result.driveUid,
    timestamp: result.timestamp,
    selectedItems: result.request.wardrobeItems,
    selectedStyle: character.selectedStyle,
    selectedPose: character.selectedPose,
    selectedBackground: character.selectedBackground,
    selectedHairstyle: character.selectedHairstyle,
    selectedHairColor: character.selectedHairColor,
    selectedRetouchPresets: character.selectedRetouchPresets,
  };
}

const selector = (s: IAppStore) => ({
  characters: s.library.characters,
  activeCharacterId: s.library.activeCharacterId,
  activeLookId: s.library.activeLookId,
  isSaving: s.library.isSaving,
  saveError: s.library.saveError,
});

export function useLibrary() {
  const { characters, activeCharacterId, activeLookId, isSaving, saveError } = useAppStore(selector);

  /** Save a character image to the library (called on upload). Returns the character ID. */
  const saveCharacterToLibrary = useCallback((characterImageUrl: string, gender: Gender): string => {
    return getAppStore().addCharacterToLibrary(characterImageUrl, gender);
  }, []);

  /**
   * Save a generation result as a look variant under the given character.
   * Prefer the explicit characterId — URL-based lookup is unreliable because
   * blob:// URLs get swapped to CDN URLs mid-generation, and a stale URL won't
   * find the in-memory character anymore.
   */
  const saveLookToLibrary = useCallback((result: GenerationResult, characterId: string | null, characterImageUrl: string, gender: Gender) => {
    const store = getAppStore();
    const { library } = store;

    let charId: string | null = characterId && library.characters.some(c => c.id === characterId) ? characterId : null;

    if (!charId) {
      const active = library.activeCharacterId;
      if (active && library.characters.some(c => c.id === active)) {
        charId = active;
      }
    }

    if (!charId) {
      const existingChar = library.characters.find(c => c.characterImageUrl === characterImageUrl);
      charId = existingChar ? existingChar.id : store.addCharacterToLibrary(characterImageUrl, gender);
    }

    const variant = toVariant(result);
    store.addLookToCharacter(charId, variant);
  }, []);

  const selectLook = useCallback((characterId: string, lookId: string) => {
    getAppStore().selectLook(characterId, lookId);
  }, []);

  const selectCharacter = useCallback((characterId: string) => {
    getAppStore().selectCharacter(characterId);
  }, []);

  const deleteCharacter = useCallback((characterId: string) => {
    getAppStore().deleteCharacter(characterId);
  }, []);

  const deleteLook = useCallback((characterId: string, lookId: string) => {
    getAppStore().deleteLook(characterId, lookId);
  }, []);

  const clearActiveEntry = useCallback(() => {
    getAppStore().clearActiveEntry();
  }, []);

  return {
    characters,
    activeCharacterId,
    activeLookId,
    isSaving,
    saveError,
    saveCharacterToLibrary,
    saveLookToLibrary,
    selectLook,
    selectCharacter,
    deleteCharacter,
    deleteLook,
    clearActiveEntry,
  };
}
